import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import { LoadingState } from '../LoadingState/LoadingState';
import styles from './ReasoningTrace.module.css';

/**
 * Ported from spectrum-ui's `ReasoningTrace` block — the unmodified source sits
 * in `reference/spectrum-ui/ai-assistants/` for diffing. Upstream is Tailwind +
 * lucide-react; nothing of either survives here. Every value resolves to a
 * token, the icons come from the Figma icon set, and the type goes through
 * `Text`.
 *
 * Built code-first, then drawn in Figma from this file as `Reasoning Trace`
 * (750:10938), which is now the reference. The 2026-09-19 redesign for DeepDive
 * AI replaced the sparkle with a `Loading State` grid and dropped the shimmer
 * from the label — see the summary below.
 *
 * Upstream ships two variants, `Steps` (a numbered `<ol>`) and `Reasoning` (the
 * steps joined into a paragraph). Only `Reasoning` is ported, so there is no
 * `variant` prop — a variant nobody asked for is a variant that rots.
 */

/** One unit of chain-of-thought. Mirrors upstream's `ReasoningStep`. */
export type ReasoningStep = {
  id: string;
  content: string;
};

export type ReasoningTraceProps = {
  /** Joined in order into a single paragraph, space-separated. */
  steps: ReasoningStep[];
  /** `thinking` runs the live clock and animates the grid; `complete` holds it still. */
  status: 'thinking' | 'complete';
  /**
   * What to show once `status` is `complete`. Falls back to whatever the clock
   * measured, which is only right if this instance was mounted for the whole
   * run — pass the real figure when the trace arrives already finished.
   */
  durationMs?: number;
  defaultOpen?: boolean;
  /**
   * Drop the 440 ceiling and fill the column. The DeepDive AI screens draw the
   * instance at 687 (the chat column's 7 tracks), wider than the set's 440.
   */
  fill?: boolean;
  className?: string;
};

/**
 * Tenths of a second since `active` last went true, ticking while it stays true.
 *
 * Counts in tenths and divides on the way out rather than holding a float,
 * because accumulating `+= 0.1` drifts off binary fractions within a few
 * seconds and starts rendering 4.300000000000001.
 */
function useThinkingClock(active: boolean) {
  const [tenths, setTenths] = useState(0);
  const wasActive = useRef(active);

  useEffect(() => {
    if (active && !wasActive.current) setTenths(0);
    wasActive.current = active;
    if (!active) return;
    const id = setInterval(() => setTenths((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [active]);

  return tenths / 10;
}

export function ReasoningTrace({
  steps,
  status,
  durationMs,
  defaultOpen = false,
  fill = false,
  className,
}: ReasoningTraceProps) {
  const [open, setOpen] = useState(defaultOpen);
  const thinking = status === 'thinking';
  const elapsed = useThinkingClock(thinking);
  const regionId = useId();

  const seconds = thinking
    ? elapsed.toFixed(1)
    : ((durationMs ?? elapsed * 1000) / 1000).toFixed(1);

  return (
    <div className={[styles.trace, fill ? styles.fill : null, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.summary}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={regionId}
      >
        {/*
          * Figma's `Loading State` instance (2059:24920): `Pattern=Orbit`, the
          * cells recoloured to `component/loading-state/dot-accent`, the label
          * and timer layers off — the same overrides `Card`'s loading state
          * makes. It replaced the sparkle, and it is the thing that moves now;
          * the word no longer shimmers. Held still once the trace is complete,
          * where the set still draws it.
          *
          * `aria-hidden` on the wrapper: the component is a `role="status"`
          * named "Loading", and inside a button that name would be read as part
          * of the button's label. The button's own words already say it.
          */}
        <span className={styles.loader} aria-hidden>
          <LoadingState
            pattern="orbit"
            tone="accent"
            showLabel={false}
            showElapsed={false}
            animate={thinking}
          />
        </span>
        {/*
          * Plain spans rather than `Text`, following Button's `.label`: `Text`
          * always applies a colour class, and the label takes its colour from
          * the button's state. Going through it would leave the two rules at
          * equal specificity and let stylesheet order decide the colour.
          */}
        {thinking ? (
          <>
            <span className={styles.label}>Thinking</span>
            <span className={styles.timer}>{seconds}s</span>
          </>
        ) : (
          <span className={styles.label}>Thought for {seconds}s</span>
        )}
        <Icon name="chevronDown" className={open ? styles.chevronOpen : styles.chevron} />
      </button>

      {/*
        * A 0fr → 1fr grid row is what animates: `height: auto` is not
        * interpolable, and a max-height guess either clips a long trace or
        * spends the transition animating empty space. The inner wrapper owns
        * the `overflow: hidden` — putting it on the grid itself leaves the row
        * unable to collapse.
        *
        * `inert` while closed keeps the collapsed text out of the accessibility
        * tree and off the tab order. `hidden` would do the same and cancel the
        * animation; visually-hidden alone would leave a screen reader reading
        * out reasoning the sighted user has folded away.
        */}
      <div
        id={regionId}
        className={open ? styles.regionOpen : styles.region}
        inert={!open}
      >
        <div className={styles.regionInner}>
          <div className={styles.body}>
            <Text as="p" size="sm" leading="normal" color="tertiary" className={styles.paragraph}>
              {steps.map((step) => step.content).join(' ')}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
